import React, { FC, useState, useEffect } from 'react'
import { useField, useFormikContext } from 'formik'
import AutoSuggest, { IProps as AutoSuggestProps } from './AutoSuggest'
import { AutoSuggestOption } from './types'
import { withValidationFields } from '../withField'

interface AutoSuggestFieldProps extends AutoSuggestProps {
  validate?: (value: string) => Promise<string | undefined>
  validateMessage?: string
  required?: boolean
  name: string
}

const AutoSuggestWithValidationFields = withValidationFields(AutoSuggest)

const AutoSuggestField: FC<AutoSuggestFieldProps> = ({
  name,
  options,
  label,
  disabled,
  loading,
  legend,
  placeholder,
  numOfSuggestionsText,
  endAdornment,
  validate,
  required,
  validateMessage,
}: AutoSuggestFieldProps) => {
  const [inputValue, setInputValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [suggestions, setSuggestions] = useState(options)
  const [
    selectedSuggestion,
    setSelectedSuggestion,
  ] = useState<AutoSuggestOption>()

  const [
    field,
    { error, touched },
    { setValue, setTouched },
  ] = useField<string>({
    name,
    validate: async (newValue) => {
      if (validate) {
        const validationError = await validate(newValue)

        if (validationError) {
          return validationError
        }
      }

      if (required && !newValue) {
        return validateMessage
      }

      if (inputValue !== selectedSuggestion?.label) {
        return validateMessage
      }

      return undefined
    },
  })

  const { status = {}, handleChange } = useFormikContext()
  const fieldStatus: string[] | undefined = status[name]
  const hasStatus = fieldStatus && fieldStatus.length > 0
  const hasError = Boolean(touched && (error || hasStatus))
  const fieldErrors: string[] = []

  if (hasError) {
    if (error) {
      fieldErrors.push(error)
    } else if (hasStatus) {
      fieldErrors.push(...(fieldStatus as string[]))
    }
  }

  useEffect(() => {
    const newSuggestions = options.filter(
      (option) =>
        option.label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1
    )
    setSuggestions(newSuggestions)

    if (inputValue !== selectedSuggestion?.label) {
      setSelectedSuggestion(undefined)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue])

  useEffect(() => {
    if (selectedSuggestion) {
      // We still want to keep existing input value
      // even if the selectedSuggestion is undefined
      setInputValue(selectedSuggestion?.label || '')
    }

    setValue(selectedSuggestion?.value || '')

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSuggestion])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsOpen(true)
    setInputValue(event.target.value)
    handleChange(event)
  }

  const handleSuggestionSelect = (suggestionItem: AutoSuggestOption | null) => {
    setIsOpen(false)

    if (!suggestionItem) {
      return
    }

    setSelectedSuggestion(suggestionItem)
  }

  const onFocus = () => {
    setIsOpen(true)
  }

  const onBlur = () => {
    setTouched(true)
    setIsOpen(false)
  }

  return (
    <>
      <AutoSuggestWithValidationFields
        disabled={disabled}
        legend={legend}
        loading={loading}
        options={suggestions}
        onInputChange={handleInputChange}
        inputValue={inputValue}
        onSelect={handleSuggestionSelect}
        label={label}
        isOpen={isOpen}
        placeholder={placeholder}
        numOfSuggestionsText={numOfSuggestionsText}
        endAdornment={endAdornment}
        field={field}
        error={hasError}
        errors={fieldErrors}
        onBlur={onBlur}
        onFocus={onFocus}
      />
      <input
        type="hidden"
        name={name}
        defaultValue={selectedSuggestion?.value}
      />
    </>
  )
}

export default AutoSuggestField
